import { useLayout } from 'hooks/useLayout'
import { Modal } from 'components/Modal/Modal'
import { Box, Button } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ActivityButton } from 'components/ActivityButton'
import { usePostPatronBadgeReward } from 'hooks/api/Contests/BadgeReward/usePostPatronBadgeReward'
import { DataTable } from 'components/Shared/DataTable'
import { SectionHeader } from 'components/SectionHeader'
import { formatFullDateTime } from 'utils/util'
import GiftIcon from 'assets/gift_empty_state.svg'
import { useSnackbar } from 'stores/useSnackbar'
import { type BadgeReward, type PatronBadgeReward } from 'types/api'

const previousBadgesEarnedColumns = (): GridColDef[] => {
  return [
    {
      field: 'createdOn',
      headerName: 'Date Earned',
      minWidth: 120,
      type: 'dateTime',
      sortable: true,
      flex: 1.5,
      sortingOrder: ['desc', 'asc'],
      valueFormatter: (params: { value: string }) => {
        const date = formatFullDateTime(params.value)
        return date
      },
    },
  ]
}

export const PatronLocationBadgeDetailsModal = ({
  patronId,
  selectedBadgeReward,
  previousBadgesEarned,
  isModalOpen,
  toggleModalIsOpen,
}: {
  patronId: number
  selectedBadgeReward: BadgeReward | null
  previousBadgesEarned: PatronBadgeReward[]
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '80%'
  const setSnackbarMessage = useSnackbar((state) => state.setMessage)

  const usePostPatronBadgeRewardMutation = usePostPatronBadgeReward({
    patronId,
    badgeRewardId: selectedBadgeReward?.id ?? 0,
    onSuccess: async () => {
      setSnackbarMessage(`Successfully awarded badge to patron`, 'success')
      toggleModalIsOpen()
    },
    onError: () =>
      setSnackbarMessage(`Error: Cannot save Patron badge reward`, 'error'),
  })

  const handleSave = () => {
    if (!usePostPatronBadgeRewardMutation.isPending) {
      usePostPatronBadgeRewardMutation.mutate({
        patronId,
        badgeRewardId: selectedBadgeReward?.id ?? 0,
      })
    }
  }

  const onModalClose = () => {
    toggleModalIsOpen()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '65%',
        height: heightModal,
      }}
    >
      <Box>
        <ModalFullHeader
          toggleIsOpen={toggleModalIsOpen}
          title={`${selectedBadgeReward?.name} Badge Details`}
        />
        <ModalFullBody heightModal={heightModal}>
          <form>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ActivityButton
                active={usePostPatronBadgeRewardMutation.isPending}
                type="button"
                variant="contained"
                disabled={!selectedBadgeReward?.id}
                onClick={handleSave}
              >
                Award Badge to Patron
              </ActivityButton>
            </div>
          </form>
          <SectionHeader
            sx={{ paddingTop: 2 }}
            title="Dates Patron Earned This Badge"
            showButton={false}
          />
          <DataTable
            columns={previousBadgesEarnedColumns()}
            rows={previousBadgesEarned}
            sortFieldName="createdOn"
            sort="desc"
            noDataMessage="Patron has not earned this badge previously."
            emptyDataImageSource={GiftIcon}
          />
        </ModalFullBody>
        <ModalFullFooter>
          <Button onClick={() => onModalClose()}>Close</Button>
        </ModalFullFooter>
      </Box>
    </Modal>
  )
}
